import React, { useEffect, useRef } from 'react';
import { Checkbox, CloseButton, Group, Loader, Stack, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import AddIcon from '@material-ui/icons/Add';
import ReceiptsIcon from '../assets/images/module/general/ReceiptsIconGray.svg';
import RentIcon from '../assets/images/module/general/RentIconGray.svg';

const ACCENT = '#5555FF';
const TEXT = '#111147';
const MUTED = '#727293';
const BORDER = '#DADAE5';
const FONT = 'Inter, sans-serif';

const labelStyle = { fontFamily: FONT, fontSize: '14px', fontWeight: 300, color: '#000' };
const smallStyle = { fontFamily: FONT, fontSize: '12px', fontWeight: 400, lineHeight: '18px' };
// "wenn" and "dann" sit level with the first row of their block: that row is FIRST_ROW_HEIGHT
// tall with its content centred, and the label shares the line height.
export const FIRST_ROW_HEIGHT = '28px';
const sectionLabelStyle = { fontFamily: FONT, fontSize: '13px', fontWeight: 500, color: MUTED, lineHeight: FIRST_ROW_HEIGHT };
const checkboxClassNames = { input: 'tenants-list-checkbox-rental-income-input', inner: 'tenants-list-checkbox-rental-income-inner' };

const TYPES = {
  Expenditure: { label: 'Ausgabe', icon: ReceiptsIcon, background: '#E3E3FC' },
  Rent: { label: 'Mieteinnahme', icon: RentIcon, background: 'rgb(247, 227, 205)' },
};

function TypeBadge({ transactionType }) {
  const type = TYPES[transactionType] || TYPES.Expenditure;
  return (
    <Group gap={6} wrap="nowrap" style={{ background: type.background, borderRadius: '10px', padding: '2px 10px' }}>
      <img src={type.icon} alt="" style={{ width: '12px', height: '12px' }} />
      <span style={{ fontFamily: FONT, fontSize: '12px', fontWeight: 500, color: TEXT }}>{type.label}</span>
    </Group>
  );
}

// One mapping as a card that reads as a sentence: "wenn <condition> / dann <action>".
// `conditions` and `action` are rendered by the caller — the action's inline menus live in
// AssignTransactionModal. A draft is a new rule for a payment without IBAN: it has nothing to
// be created from until a text is entered, so it shows dashed until then.
export function TransactionMappingCard({ transactionType, conditions, action, draft = false, readOnly = false, saving = false, onDelete }) {
  return (
    <div style={{ width: '100%', background: '#FFFFFF', border: `1px ${draft ? 'dashed' : 'solid'} ${draft ? ACCENT : BORDER}`, borderRadius: '8px', padding: '12px 14px' }}>
      <Group justify="space-between" wrap="nowrap" mb={24}>
        <Group gap={8} wrap="nowrap">
          <TypeBadge transactionType={transactionType} />
          {draft && <span style={{ ...smallStyle, color: MUTED }}>Neue Regel · wird mit dem Verwendungszweck angelegt</span>}
        </Group>
        {saving
          ? <Loader size="xs" />
          : (!readOnly && onDelete) &&
            <Tooltip label={draft ? 'Neue Regel entfernen' : 'Regel löschen'} zIndex={999999} withinPortal>
              <CloseButton size="sm" onClick={onDelete} aria-label={draft ? 'Neue Regel entfernen' : 'Regel löschen'} />
            </Tooltip>}
      </Group>

      <div style={{ display: 'grid', gridTemplateColumns: '44px minmax(0, 1fr)', columnGap: '12px', rowGap: '10px', alignItems: 'start' }}>
        <span style={sectionLabelStyle}>wenn</span>
        <div style={{ minWidth: 0 }}>{conditions}</div>
        <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #EFEFF5' }} />
        <span style={sectionLabelStyle}>dann</span>
        <div style={{ minWidth: 0 }}>{action}</div>
      </div>
    </div>
  );
}

// The editable condition of a mapping: this transaction's IBAN and/or a text the purpose has
// to contain, each behind its own checkbox. Stateless — the caller holds what is being edited
// and decides when to save (`onIbanChange`, `onTextToggle`, `onTextBlur`).
export function MappingConditionFields({ iban, counterpartName, ibanChecked, textChecked, text, error, disabled = false, onIbanChange, onTextToggle, onTextChange, onTextBlur }) {
  const useIban = ibanChecked && !!iban;
  const textInputRef = useRef(null);
  const wasTextChecked = useRef(textChecked);

  // Ticking the box means "I want to type a text now".
  useEffect(() => {
    if(textChecked && !wasTextChecked.current){
      textInputRef.current?.focus();
    }
    wasTextChecked.current = textChecked;
  }, [textChecked]);

  return (
    <Stack gap={8}>
      <div style={{ display: 'flex', alignItems: 'center', minHeight: FIRST_ROW_HEIGHT }}>
        <Checkbox
          color={ACCENT}
          classNames={checkboxClassNames}
          checked={useIban}
          disabled={disabled || !iban}
          onChange={(event) => onIbanChange(event.currentTarget.checked)}
          // The name only ever moves to the next line as a whole, never split mid-word.
          label={iban
            ? <span style={labelStyle}>IBAN <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{iban}</span>{counterpartName && <>{' '}<span style={{ whiteSpace: 'nowrap' }}>({counterpartName})</span></>}</span>
            : <span style={{ ...labelStyle, color: MUTED }}>Diese Zahlung hat keine IBAN</span>}
        />
      </div>
      {/* The same Checkbox as the IBAN row, so both labels start at the same x; centred against
          the field. The field drops below when the card is too narrow for it beside the label. */}
      <Group gap={8} align="center">
        <Checkbox
          color={ACCENT}
          classNames={checkboxClassNames}
          checked={textChecked}
          disabled={disabled}
          onChange={(event) => onTextToggle(event.currentTarget.checked)}
          label={<span style={{ ...labelStyle, whiteSpace: 'nowrap' }}>{useIban ? 'und Verwendungszweck enthält' : 'Verwendungszweck enthält'}</span>}
        />
        <TextInput
          ref={textInputRef}
          size="xs"
          style={{ flex: '1 1 160px' }}
          classNames={{ input: 'transaction-mapping-text-input' }}
          placeholder="z. B. Hausgeld"
          value={text}
          disabled={disabled || !textChecked}
          error={!!error}
          onChange={(event) => onTextChange(event.currentTarget.value)}
          onBlur={onTextBlur}
          aria-label="Verwendungszweck enthält"
        />
      </Group>
      {/* Lines up with the checkbox labels (checkbox plus Mantine's label offset). */}
      {error && <Text style={{ ...smallStyle, color: '#E8193C', paddingLeft: '28px' }}>{error}</Text>}
    </Stack>
  );
}

// A saved mapping's condition in one line, e.g. "IBAN DE89… und Verwendungszweck enthält
// „Hausgeld“". Mappings saved before 2506 render as IBAN-only.
export function MappingConditionSummary({ mapping, style }) {
  const parts = [];
  if(mapping?.counterpartIban){
    parts.push(<>IBAN <span style={{ fontWeight: 500 }}>{mapping.counterpartIban}</span></>);
  }
  if(mapping?.textQualifier){
    parts.push(<>Verwendungszweck enthält <span style={{ fontWeight: 500 }}>„{mapping.textQualifier}“</span></>);
  }
  return (
    <span style={{ ...labelStyle, ...style }}>
      {parts.length === 0 ? '–' : parts.map((part, index) => (
        <React.Fragment key={index}>{index > 0 && ' und '}{part}</React.Fragment>
      ))}
    </span>
  );
}

export function AddMappingButton({ label, onClick, disabled = false }) {
  return (
    <UnstyledButton
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
        border: `1px dashed ${disabled ? BORDER : '#B9B9F5'}`, borderRadius: '8px', padding: '9px 14px',
        fontFamily: FONT, fontSize: '14px', fontWeight: 400, color: disabled ? '#9A9AB5' : ACCENT,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <AddIcon style={{ fontSize: '16px' }} />
      {label}
    </UnstyledButton>
  );
}
